import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

// Выносим LogsModal в отдельный компонент вне App
const LogsModal = React.memo(({ logs, taskId, onClose, setSelectedTaskLogs }) => {
    const [autoScroll, setAutoScroll] = useState(true);
    const [fontSize, setFontSize] = useState(14);
    const [showFontSize, setShowFontSize] = useState(false);
    const logsRef = useRef(null);
    const prevLogsRef = useRef(logs);
    const scrollPositionRef = useRef(0);

    const updateLogs = useCallback(async () => {
        if (!autoScroll && logsRef.current) {
            scrollPositionRef.current = logsRef.current.scrollTop;
        }

        try {
            const response = await fetch(`/api/tasks/${taskId}/logs-content`);
            const newLogs = await response.text();
            if (newLogs !== prevLogsRef.current) {
                prevLogsRef.current = newLogs;
                setSelectedTaskLogs(newLogs);

                if (!autoScroll && logsRef.current) {
                    requestAnimationFrame(() => {
                        if (logsRef.current) {
                            logsRef.current.scrollTop = scrollPositionRef.current;
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Error updating logs:', error);
        }
    }, [taskId, setSelectedTaskLogs, autoScroll]);

    useEffect(() => {
        if (autoScroll && logsRef.current) {
            logsRef.current.scrollTop = logsRef.current.scrollHeight;
        }
    }, [logs, autoScroll]);

    useEffect(() => {
        const interval = setInterval(updateLogs, 2000);
        return () => clearInterval(interval);
    }, [updateLogs]);

    const handleAutoScrollChange = useCallback((e) => {
        const newValue = e.target.checked;
        if (!newValue && logsRef.current) {
            scrollPositionRef.current = logsRef.current.scrollTop;
        }
        setAutoScroll(newValue);
        localStorage.setItem(`autoScroll-${taskId}`, newValue);
    }, [taskId]);

    useEffect(() => {
        const savedAutoScroll = localStorage.getItem(`autoScroll-${taskId}`);
        const savedFontSize = localStorage.getItem(`fontSize-${taskId}`);
        
        if (savedAutoScroll !== null) {
            setAutoScroll(savedAutoScroll === 'true');
        }
        if (savedFontSize !== null) {
            setFontSize(Number(savedFontSize));
        }

        const handleClickOutside = (e) => {
            if (!e.target.closest('.font-size-dropdown')) {
                setShowFontSize(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [taskId]);

    const handleFontSizeClick = useCallback((e) => {
        e.stopPropagation();
        setShowFontSize(prev => !prev);
    }, []);

    const handleFontSizeSelect = useCallback((size, e) => {
        e.stopPropagation();
        setFontSize(size);
        localStorage.setItem(`fontSize-${taskId}`, size);
        setShowFontSize(false);
    }, [taskId]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh]" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold">Logs for task {taskId}</h3>
                    <div className="space-x-4 flex items-center">
                        <div className="relative font-size-dropdown">
                            <button
                                onClick={handleFontSizeClick}
                                className="border rounded px-2 py-1"
                            >
                                {fontSize}px ▼
                            </button>
                            {showFontSize && (
                                <div className="absolute top-full left-0 mt-1 bg-white border rounded shadow-lg z-50">
                                    {[12, 14, 16, 18].map(size => (
                                        <button
                                            key={size}
                                            className="block w-full px-4 py-2 text-left hover:bg-gray-100"
                                            onClick={(e) => handleFontSizeSelect(size, e)}
                                        >
                                            {size}px
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id={`autoscroll-${taskId}`}
                                checked={autoScroll}
                                onChange={handleAutoScrollChange}
                                className="mr-2"
                            />
                            <label htmlFor={`autoscroll-${taskId}`}>Auto-scroll</label>
                        </div>

                        <button
                            onClick={() => window.open(`/api/tasks/${taskId}/logs`)}
                            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                        >
                            Download
                        </button>
                        <button
                            onClick={onClose}
                            className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
                        >
                            Close
                        </button>
                    </div>
                </div>
                <pre
                    ref={logsRef}
                    className="bg-gray-100 p-4 rounded overflow-auto h-[60vh]"
                    style={{ fontSize: `${fontSize}px` }}
                >
                    {logs}
                </pre>
            </div>
        </div>
    );
});

export default function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [activeTab, setActiveTab] = useState('create');
    const [tasks, setTasks] = useState([]);
    const [files, setFiles] = useState(null);
    const [globalSession, setGlobalSession] = useState(null);
    const [launchDate, setLaunchDate] = useState('');
    const [note, setNote] = useState('');
    const [apiConfigs, setApiConfigs] = useState([]);
    const [selectedApiConfig, setSelectedApiConfig] = useState('');
    const [sessionTimer, setSessionTimer] = useState(null);
    const [modalState, setModalState] = useState({
        logs: null,
        taskId: null,
        isOpen: false
    });
    const [sortConfig, setSortConfig] = useState({
        key: 'id',
        direction: 'desc'
    });


    const checkSession = useCallback(async () => {
        // Добавляем дебаунсинг
        if (window.sessionCheckTimeout) {
            clearTimeout(window.sessionCheckTimeout);
        }
    
        window.sessionCheckTimeout = setTimeout(async () => {
            try {
                const response = await fetch('/api/check-session', {
                    credentials: 'include'
                });
                
                if (!response.ok || response.status === 401) {
                    setIsLoggedIn(false);
                    localStorage.removeItem('isLoggedIn');
                    setFiles(null);
                    setGlobalSession(null);
                    setLaunchDate('');
                    setNote('');
                    if (sessionTimer) {
                        clearInterval(sessionTimer);
                        setSessionTimer(null);
                    }
                    return;
                }
    
                const data = await response.json();
                if (!data.authenticated) {
                    setIsLoggedIn(false);
                    localStorage.removeItem('isLoggedIn');
                }
            } catch (error) {
                console.error('Error checking session:', error);
            }
        }, 1000);
    }, [sessionTimer]);

    const fetchApiConfigs = useCallback(async () => {
        try {
            const response = await fetch('/api/api-configs');
            const data = await response.json();
            setApiConfigs(data);
        } catch (error) {
            console.error('Error fetching API configs:', error);
        }
    }, []);

    const fetchTasks = useCallback(async () => {
        try {
            console.log('Fetching tasks...');
            const response = await fetch('/api/tasks');
            const data = await response.json();
            console.log('Fetched tasks:', data);
            setTasks(data);
        } catch (error) {
            console.error('Error fetching tasks:', error);
        }
    }, []);

    const startSessionTimer = useCallback(() => {
        if (sessionTimer) {
            clearInterval(sessionTimer);
        }
        
        // Проверяем сессию каждые 5 минут вместо каждой минуты
        const timer = setInterval(() => {
            checkSession();
        }, 300000); // 5 минут
        
        setSessionTimer(timer);
    }, [checkSession, sessionTimer]);

    const handleOpenLogs = useCallback(async (taskId) => {
        try {
            const response = await fetch(`/api/tasks/${taskId}/logs-content`);
            const logs = await response.text();
            setModalState({
                logs,
                taskId,
                isOpen: true
            });
        } catch (error) {
            console.error('Error fetching logs:', error);
        }
    }, []);

    const handleCloseLogs = useCallback(() => {
        setModalState({
            logs: null,
            taskId: null,
            isOpen: false
        });
    }, []);

    
    useEffect(() => {
        let intervalId;
        let statusCheckId;
        
        if (isLoggedIn) {
            // Начальная загрузка данных
            fetchTasks();
            fetchApiConfigs();
            
            // Интервал для проверки сессии
            intervalId = setInterval(() => {
                if (document.visibilityState === 'visible') {
                    checkSession();
                }
            }, 30000);
    
            // Отдельный интервал для обновления статусов задач
            statusCheckId = setInterval(() => {
                if (document.visibilityState === 'visible') {
                    const response = fetch('/api/tasks/statuses');
                    response.then(res => res.json())
                        .then(statuses => {
                            setTasks(prevTasks => 
                                prevTasks.map(task => ({
                                    ...task,
                                    status: statuses[task.id]?.status || task.status,
                                    messages: statuses[task.id]?.messages || task.messages
                                }))
                            );
                        })
                        .catch(error => console.error('Error updating task statuses:', error));
                }
            }, 3000); // Проверка каждые 3 секунды
        }
    
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
            if (statusCheckId) {
                clearInterval(statusCheckId);
            }
        };
    }, [isLoggedIn, fetchTasks, fetchApiConfigs, checkSession]);

    useEffect(() => {
        let sessionTimeoutId;
        let activityTimeoutId;
    
        if (isLoggedIn) {
            const handleActivity = () => {
                if (activityTimeoutId) {
                    clearTimeout(activityTimeoutId);
                }
                
                activityTimeoutId = setTimeout(() => {
                    checkSession();
                }, 5000); // Проверяем сессию не чаще чем раз в 5 секунд
            };
    
            const activityEvents = ['click', 'keypress', 'scroll', 'mousemove', 'touchstart'];
            activityEvents.forEach(event => {
                document.addEventListener(event, handleActivity);
            });
    
            // Регулярная проверка сессии
            sessionTimeoutId = setInterval(() => {
                checkSession();
            }, 300000); // Каждые 5 минут
    
            return () => {
                activityEvents.forEach(event => {
                    document.removeEventListener(event, handleActivity);
                });
                if (sessionTimeoutId) {
                    clearInterval(sessionTimeoutId);
                }
                if (activityTimeoutId) {
                    clearTimeout(activityTimeoutId);
                }
            };
        }
    }, [isLoggedIn, checkSession]);



    useEffect(() => {
        const checkInitialSession = async () => {
            try {
                const response = await fetch('/api/check-session', {
                    credentials: 'include'
                });
                
                if (!response.ok || response.status === 401) {
                    setIsLoggedIn(false);
                    localStorage.removeItem('isLoggedIn');
                    return;
                }
    
                const data = await response.json();
                if (data.authenticated) {
                    setIsLoggedIn(true);
                } else {
                    setIsLoggedIn(false);
                    localStorage.removeItem('isLoggedIn');
                }
            } catch (error) {
                console.error('Error checking initial session:', error);
                setIsLoggedIn(false);
                localStorage.removeItem('isLoggedIn');
            }
        };
    
        const savedLoginState = localStorage.getItem('isLoggedIn');
        if (savedLoginState === 'true') {
            checkInitialSession();
        } else {
            setIsLoggedIn(false);
        }
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    username, 
                    password,
                    remember: true
                }),
                credentials: 'include'
            });
    
            const data = await response.json();
            if (data.success) {
                setIsLoggedIn(true);
                localStorage.setItem('isLoggedIn', 'true');
                startSessionTimer();  // Запускаем таймер после успешного входа
            }
        } catch (error) {
            console.error('Login error:', error);
        }
    };

    const handleLogout = async () => {
        try {
            await fetch('/api/logout', {
                method: 'POST',
                credentials: 'include'
            });
            
            setIsLoggedIn(false);
            localStorage.removeItem('isLoggedIn');
            if (sessionTimer) {
                clearInterval(sessionTimer);
                setSessionTimer(null);
            }
        } catch (error) {
            console.error('Logout error:', error);
        }
    };


    const handleCreateTask = async (e) => {
        e.preventDefault();
        try {
            console.log('Starting task creation...');
            const formData = new FormData();
            formData.append('launch_date', launchDate);
            formData.append('note', note);
            formData.append('api_config', selectedApiConfig);
            
            if (files) {
                Array.from(files).forEach(file => {
                    const relativePath = file.webkitRelativePath;
                    formData.append('files', file, relativePath);
                });
            }
            
            if (globalSession) {
                Array.from(globalSession).forEach(file => {
                    const relativePath = file.webkitRelativePath;
                    formData.append('global_session', file, relativePath);
                });
            }
    
            const response = await fetch('/api/tasks', {
                method: 'POST',
                body: formData,
            });
            
            if (response.ok) {
                console.log('Task created successfully');
                setFiles(null);
                setGlobalSession(null);
                setLaunchDate('');
                setNote('');
                e.target.reset();
                fetchTasks();
                alert('Task created successfully!');
            } else {
                const errorText = await response.text();
                let errorMessage;
                try {
                    const errorJson = JSON.parse(errorText);
                    errorMessage = errorJson.error || 'Unknown error';
                } catch {
                    errorMessage = errorText || 'Failed to create task';
                }
                console.error('Server error:', errorMessage);
                alert('Error creating task: ' + errorMessage);
            }
        } catch (error) {
            console.error('Error creating task:', error);
            alert('Error creating task: ' + error.message);
        }
    };

    const handleDeleteTask = async (taskId) => {
        try {
            const response = await fetch(`/api/tasks/${taskId}`, {
                method: 'DELETE',
            });
            if (response.ok) {
                fetchTasks();
            }
        } catch (error) {
            console.error('Error deleting task:', error);
        }
    };
    
    const handleDownloadSentMessages = async (taskId) => {
        try {
            const response = await fetch(`/api/tasks/${taskId}/sent-messages`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            
            // Получаем содержимое файла
            const blob = await response.blob();
            
            // Создаем URL для скачивания
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'sent_messages.json';
            a.style.display = 'none';
            
            document.body.appendChild(a);
            a.click();
            
            // Очищаем
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading sent messages file:', error);
            alert('Ошибка при скачивании файла результатов');
        }
    };
    
    const sortTasks = (tasks) => {
        return [...tasks].sort((a, b) => {
            if (sortConfig.key === 'id') {
                return sortConfig.direction === 'desc' 
                    ? b.id.localeCompare(a.id)
                    : a.id.localeCompare(b.id);
            }
            if (sortConfig.key === 'launch_date') {
                const dateA = new Date(a.launch_date);
                const dateB = new Date(b.launch_date);
                return sortConfig.direction === 'desc' 
                    ? dateB - dateA
                    : dateA - dateB;
            }
            return 0;
        });
    };

    const requestSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
        }));
    };

    const memoizedLogsModal = useMemo(() => 
            modalState.isOpen ? (
                <LogsModal
                    logs={modalState.logs}
                    taskId={modalState.taskId}
                    setSelectedTaskLogs={(newLogs) => 
                        setModalState(prev => ({
                            ...prev,
                            logs: newLogs
                        }))
                    }
                    onClose={handleCloseLogs}
                />
            ) : null
        , [modalState, handleCloseLogs]);

    return (
        <div className="container mx-auto p-4">
            {!isLoggedIn ? (
                <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                    <div className="bg-white p-8 rounded-lg shadow-md w-96">
                        <h2 className="text-2xl mb-6 text-center font-bold">Login</h2>
                        <form onSubmit={handleLogin}>
                            <div className="mb-4">
                                <input
                                    type="text"
                                    placeholder="Username"
                                    className="w-full p-2 border rounded"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                />
                            </div>
                            <div className="mb-6">
                                <input
                                    type="password"
                                    placeholder="Password"
                                    className="w-full p-2 border rounded"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
                            >
                                Login
                            </button>
                        </form>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex mb-4 border-b">
                            <button
                                className={`px-4 py-2 ${activeTab === 'create' ? 'border-b-2 border-blue-500' : ''}`}
                                onClick={() => {
                                    setActiveTab('create');
                                }}
                            >
                                Create Task
                            </button>
                            <button
                                className={`px-4 py-2 ${activeTab === 'list' ? 'border-b-2 border-blue-500' : ''}`}
                                onClick={() => {
                                    setActiveTab('list');
                                }}
                            >
                                Task List
                            </button>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                        >
                            Выйти
                        </button>
                    </div>

                    {activeTab === 'create' ? (
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h2 className="text-2xl mb-6">Create New Task</h2>
                            <form onSubmit={handleCreateTask} encType="multipart/form-data">
                                <div className="mb-4">
                                    <label className="block mb-2">Files Folder</label>
                                    <input
                                        type="file"
                                        webkitdirectory="true"
                                        directory="true"
                                        multiple
                                        onChange={(e) => {
                                            setFiles(e.target.files);
                                        }}
                                        className="w-full p-2 border rounded"
                                    />
                                </div>
                                
                                <div className="mb-4">
                                    <label className="block mb-2">Global Session Folder</label>
                                    <input
                                        type="file"
                                        webkitdirectory="true"
                                        directory="true"
                                        multiple
                                        onChange={(e) => {
                                            setGlobalSession(e.target.files);
                                        }}
                                        className="w-full p-2 border rounded"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="block mb-2">Launch Date</label>
                                    <input
                                        type="date"
                                        value={launchDate}
                                        onChange={(e) => {
                                            setLaunchDate(e.target.value);
                                        }}
                                        className="w-full p-2 border rounded"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="block mb-2">API Configuration</label>
                                    <select
                                        value={selectedApiConfig}
                                        onChange={(e) => {
                                            setSelectedApiConfig(e.target.value);
                                        }}
                                        className="w-full p-2 border rounded"
                                        required
                                    >
                                        <option value="">Select API Configuration</option>
                                        {apiConfigs.map(config => (
                                            <option key={config.name} value={config.name}>
                                                {config.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-6">
                                    <label className="block mb-2">Note</label>
                                    <textarea
                                        value={note}
                                        onChange={(e) => {
                                            setNote(e.target.value);
                                        }}
                                        className="w-full p-2 border rounded"
                                        rows="3"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                                >
                                    Create Task
                                </button>
                            </form>
                        </div>
                    ) : (
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h2 className="text-2xl mb-6">Task List</h2>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr>
                                            <th 
                                                className="text-left p-2 border-b cursor-pointer hover:bg-gray-100"
                                                onClick={() => {
                                                    requestSort('id');
                                                }}
                                            >
                                                ID {sortConfig.key === 'id' && (
                                                    <span>{sortConfig.direction === 'desc' ? '▼' : '▲'}</span>
                                                )}
                                            </th>
                                            <th 
                                                className="text-left p-2 border-b cursor-pointer hover:bg-gray-100"
                                                onClick={() => {
                                                    requestSort('launch_date');
                                                }}
                                            >
                                                Launch Date {sortConfig.key === 'launch_date' && (
                                                    <span>{sortConfig.direction === 'desc' ? '▼' : '▲'}</span>
                                                )}
                                            </th>
                                            <th className="text-left p-2 border-b">Note</th>
                                            <th className="text-left p-2 border-b">API Config</th>
                                            <th className="text-left p-2 border-b">Messages</th>
                                            <th className="text-left p-2 border-b">Status</th>
                                            <th className="text-left p-2 border-b">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortTasks(tasks).map(task => (
                                            <tr key={task.id} className="border-b">
                                                <td className="p-2">{task.id}</td>
                                                <td className="p-2">{task.launch_date}</td>
                                                <td className="p-2">{task.note}</td>
                                                <td className="p-2">{task.api_config}</td>
                                                <td className="p-2">
                                                    {task.messages ? 
                                                        `${task.messages.total}/${task.messages.sent}` : 
                                                        '0/0'
                                                    }
                                                </td>
                                                <td className="p-2">
                                                    <span className={`inline-block px-2 py-1 rounded-full text-sm ${
                                                        task.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                                                        task.status === 'completed' ? 'bg-green-200 text-green-800' :
                                                        task.status === 'failed' ? 'bg-red-200 text-red-800' :
                                                        'bg-gray-200 text-gray-800'
                                                    }`}>
                                                        {task.status === 'pending' ? 'Ожидает выполнения' :
                                                         task.status === 'completed' ? 'Выполнено' :
                                                         task.status === 'failed' ? 'Ошибка' : 
                                                         'Неизвестно'}
                                                    </span>
                                                </td>
                                                <td className="p-2">
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => handleOpenLogs(task.id)}
                                                            className="bg-blue-500 text-white px-2 py-1 rounded text-sm hover:bg-blue-600"
                                                        >
                                                            Logs
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteTask(task.id)}
                                                            className="bg-red-500 text-white px-2 py-1 rounded text-sm hover:bg-red-600"
                                                        >
                                                            Delete
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                window.open(`/api/tasks/${task.id}/download`);
                                                            }}
                                                            className="bg-green-500 text-white px-2 py-1 rounded text-sm hover:bg-green-600"
                                                        >
                                                            Download
                                                        </button>
                                                        <button
                                                            onClick={() => handleDownloadSentMessages(task.id)}
                                                            className="bg-purple-500 text-white px-2 py-1 rounded text-sm hover:bg-purple-600"
                                                        >
                                                            Results
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {memoizedLogsModal}
                </>
            )}
        </div>
    );
}
